import { 
  PageRenderProps, 
  GamePageRegistry, 
  CommonPageId, 
  PagePuzzleArchive, 
  ArchiveDifficultyConfig
} from '@mental-mint/shared-ui';
import { PageMagicLinkCallback } from '@mental-mint/shared-ui/Pages/PageMagicLinkCallback/PageMagicLinkCallback';
import { logger, GetTodaysDate } from '@mental-mint/shared-utils';
import { WordDriftPageId } from './WordDriftPageTypes';
import { PagePlay } from './PagePlay';
import { PagePostGame } from './PagePostGame';
import { PagePuzzleIntro } from './PagePuzzleIntro';
import { LevelSelectionButton } from '../Components/LevelSelectionButton';
import { 
  getWordDriftLevelId, 
  getPuzzleByDate, 
  getPuzzleByLevelId, 
  setCurrentPuzzle, 
  getTodaysPuzzle, 
  loadPuzzles 
} from '../WordDriftUtils';
import { loadWordDriftGame } from '../Utils/saveManager';
import React from 'react';

// Word-drift has only one difficulty level per day
const WORD_DRIFT_DIFFICULTY: ArchiveDifficultyConfig[] = [
  { id: 'daily', label: 'Daily Puzzle' },
];

// Store the current puzzle key to persist across modal navigation
// Use a stable key that only changes when the puzzle actually changes (not on every selection)
let currentPuzzleKey = 'today';

/**
 * WordDrift page registry that provides page renderers.
 * Implements the common GamePageRegistry interface.
 */
export const wordDriftPageRegistry: GamePageRegistry = {
  getPageRenderers: () => ({
    [WordDriftPageId.Play]: ({ navigateToPage, navigationProps }: PageRenderProps) => {
        // Update puzzle key when new puzzle is provided
        // Use puzzle identity (start-goal words) as key, NOT timestamp
        // This avoids unnecessary remounts when re-selecting the same puzzle
        if (navigationProps?.puzzle) {
            currentPuzzleKey = `${navigationProps.puzzle.startWord}-${navigationProps.puzzle.goalWord}`;
            setCurrentPuzzle(navigationProps.puzzle);
        } else {
            // If no puzzle provided, keep using the current key (don't reset)
            currentPuzzleKey = currentPuzzleKey || 'today';
            // Don't reset currentPuzzle - let PagePlay set it when it loads
        }
        
        return (
            <PagePlay 
                key={currentPuzzleKey}
                navigateToPage={navigateToPage} 
                puzzle={navigationProps?.puzzle}
            />
        );
    },
    
    [WordDriftPageId.PuzzleIntro]: ({ onRequestClose, navigationProps }: PageRenderProps) => {
        return <PagePuzzleIntro onRequestClose={onRequestClose} puzzle={navigationProps?.puzzle} onDismiss={navigationProps?.onDismiss} />;
    },
    
    [WordDriftPageId.PostGame]: ({ navigateToPage, navigationProps }: PageRenderProps) => {
        // If gameState is provided via navigationProps, use it
        if (navigationProps?.gameState) {
            return (
                <PagePostGame 
                    gameState={navigationProps.gameState}
                    onArchiveOpen={() => navigateToPage?.(CommonPageId.Archive)}
                />
            );
        }
        
        // Otherwise, load today's completed puzzle data as fallback
        const PostGameLoader = () => {
            const [gameState, setGameState] = React.useState<any>(null);
            const [isLoading, setIsLoading] = React.useState(true);
            
            React.useEffect(() => {
                const loadTodaysPuzzleData = async () => {
                        try {
                            // Load puzzles and save data
                            await loadPuzzles();
                            const saveData = await loadWordDriftGame();
                            const todaysDate = GetTodaysDate();
                            const todaysLevelId = getWordDriftLevelId(todaysDate);
                            const todaysScore = saveData.levelScores[todaysLevelId];
                            
                            if (!todaysScore) {
                                logger.warn('[PostGame] No score found for today\'s puzzle');
                                setIsLoading(false);
                                return;
                            }
                            
                            // Get today's puzzle to extract start/goal words
                            const todaysPuzzle = getTodaysPuzzle();
                            
                            // Reconstruct minimal game state for PostGame display
                            // We only need the fields that PagePostGame actually uses
                            const reconstructedGameState = {
                                startWord: todaysPuzzle.startWord,
                                goalWord: todaysPuzzle.goalWord,
                                isComplete: true,
                                isWon: todaysScore.isWon,
                                moveCount: todaysScore.gameMetadata?.moveCount || 0,
                                hintsUsed: todaysScore.gameMetadata?.hintsUsed || 0,
                                path: todaysScore.gameMetadata?.path || [],
                                pathLength: todaysScore.gameMetadata?.pathLength || (todaysScore.gameMetadata?.path?.length || 0),
                                optimalPath: todaysPuzzle.shortestPath 
                                    ? todaysPuzzle.shortestPath.map((word: string) => ({ 
                                        variations: {
                                          display: word, 
                                          lemmatized: word.toLowerCase(),
                                          onClickDestination: word.toLowerCase()
                                        }
                                      }))
                                    : undefined,
                                timeLeftSeconds: 0,
                                timeLimitSeconds: 300,
                                currentWordPlayableInfo: {} as any, // Not used in PostGame
                            };
                            
                            setGameState(reconstructedGameState);
                        } catch (error) {
                            logger.error('[PostGame] Failed to load today\'s puzzle data:', error);
                        } finally {
                            setIsLoading(false);
                        }
                    };
                    
                    loadTodaysPuzzleData();
                }, []);
                
                if (isLoading) {
                    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>;
                }
                
                if (!gameState) {
                    return <div style={{ padding: '2rem', textAlign: 'center' }}>No puzzle data available</div>;
                }
                
                return (
                    <PagePostGame 
                        gameState={gameState}
                        onArchiveOpen={() => navigateToPage?.(CommonPageId.Archive)}
                    />
                );
            };
            
            return <PostGameLoader />;
        },
    
    [CommonPageId.Archive]: ({ navigateToPage, navigationProps }: PageRenderProps) => {
        // Custom render function for word-drift archive using LevelSelectionButton
        const renderWordDriftLevelButton = (
            date: Date,
            difficulty: any,
            levelScore: any,
            onPlayPuzzle: (date: Date, difficultyId: string) => void
        ) => {
            const puzzle = getPuzzleByDate(date);
            if (!puzzle) return null;
            
            return (
                <LevelSelectionButton
                    puzzle={puzzle}
                    levelScore={levelScore}
                    onClick={() => onPlayPuzzle(date, difficulty.id)}
                />
            );
        };
        
        return (
            <PagePuzzleArchive
                onPuzzlesSelected={(requestedLevelIds: string[]) => {
                    // Word drift only selects one puzzle at a time
                    if (requestedLevelIds.length > 0) {
                        const levelId = requestedLevelIds[0];
                        const puzzle = getPuzzleByLevelId(levelId);
                        if (puzzle) {
                            // Navigate to Play page with the selected puzzle
                            navigateToPage?.(WordDriftPageId.Play, { puzzle });
                            // Open PuzzleIntro modal to show the puzzle details
                            // Use setTimeout to ensure Play page renders first
                            setTimeout(() => {
                                navigateToPage?.(WordDriftPageId.PuzzleIntro, { puzzle });
                            }, 0);
                        }
                    }
                }}
                difficulties={WORD_DRIFT_DIFFICULTY}
                getLevelId={(date: Date) => getWordDriftLevelId(date)}
                renderLevelButton={renderWordDriftLevelButton}
            />
        );
    },
    
    // Override MagicLinkCallback to check if today's puzzle is solved after login
    [CommonPageId.MagicLinkCallback]: ({ onRequestClose, navigateToPage }: PageRenderProps) => {
      const handleLoginSuccess = async () => {
        try {
          // Check if today's puzzle is already solved
          await loadPuzzles();
          const saveData = await loadWordDriftGame();
          const todaysDate = GetTodaysDate();
          const todaysLevelId = getWordDriftLevelId(todaysDate);
          const todaysScore = saveData.levelScores[todaysLevelId];
          const isTodaysPuzzleSolved = todaysScore?.isWon === true;
          
          logger.debug('[MagicLinkCallback] Login successful, checking puzzle status:', {
            levelId: todaysLevelId,
            solved: isTodaysPuzzleSolved
          });
          
          // Close the magic link callback page first
          if (onRequestClose) {
            onRequestClose();
          }
          
          // If puzzle is solved, show PostGame modal
          if (isTodaysPuzzleSolved && navigateToPage) {
            logger.debug('[MagicLinkCallback] Today\'s puzzle solved - showing PostGame');
            setTimeout(() => {
              navigateToPage(WordDriftPageId.PostGame);
            }, 500); // Small delay to ensure callback page closes first
          }
        } catch (error) {
          logger.error('[MagicLinkCallback] Failed to check puzzle status:', error);
          // Fallback to just closing the callback page
          if (onRequestClose) {
            onRequestClose();
          }
        }
      };
      
      return (
        <PageMagicLinkCallback
          onLoginSuccess={handleLoginSuccess}
          onRequestClose={onRequestClose}
          navigateToPage={navigateToPage}
        />
      );
    },
  })
};
